<template>
  <main class="survey-page" aria-labelledby="page-title">

    <header class="page-head">
      <div class="desc">
        <p>
          본 자가진단은 조직의 <strong>지배구조(Corporate Governance)</strong> 수준을 국제 기준에 따라
          평가할 수 있도록 설계된 전문 진단 시스템입니다.
          내부감사인, 외부감사인, 감사위원회, 이사회, 최고경영진 등 핵심 지배구조 기능 5개 영역을 대상으로,
          각 기능별 역할과 운영 수준을 스스로 점검하고 개선 방향을 도출할 수 있도록 구성되어 있습니다.
        </p>

        <p>
          각 영역은 역할, 권한, 독립성, 윤리, 성과, 통제 수준, 보고 체계 등 국제 기준을 바탕으로 한
          세부 평가 항목으로 구성되어 있어, 조직의 지배구조 운영 상태를 객관적으로 확인할 수 있습니다.
        </p>

        <p>
          <strong>지배구조 자가점검을 통해 기대할 수 있는 효과</strong><br />
          - 통제 미비 요소 및 개선 필요 사항을 <strong>신속하게 파악</strong><br />
          - 조직의 내부통제·지배구조 수준을 <strong>국제 기준과 비교·평가</strong><br />
          - 향후 내부통제 고도화, 인증, 외부 감사 대응을 위한 <strong>기초자료 확보</strong>
        </p>

        <p>
          또한 내부감사인, 외부감사인, 감사위원회, 이사회, 최고경영진 등 5개 영역에 대한
          요약형 자가진단표는 무료로 제공되며,
          이를 통해 조직의 지배구조 성숙도를 확인하는 1차 진단 자료로 활용할 수 있습니다.
        </p>

        <!-- 회원 전용 안내 박스 -->
        <div class="member-note">
          <p><strong>※ 회원 전용 기능</strong></p>
          <p>
            회원은 위 5개 영역(총 245개 세부 체크리스트)에 대해 심층적인 자가점검을 수행할 수 있으며,
            보다 체계적인 진단 및 관리를 할 수 있습니다.<br />
            비회원에게는 요약된 35개 항목이 무료로 제공됩니다.
          </p>
        </div>
        <p>
          <strong>진단 방법 안내</strong><br />
          조직의 지배구조 수준을 평가하기 위해, 아래 각 영역과 항목을 신중히 검토하고,
          현재 상황에 가장 맞는 답변을 <strong>‘예’ 또는 ‘아니오’</strong>로 선택해 주세요.
        </p>

        <p>• <strong>예(Yes)</strong> : 해당 항목에 부합하거나 충족함</p>
        <p>• <strong>아니오(No)</strong> : 해당 항목에 부합하지 않거나 충족하지 못함</p>

        <p>
          ※ <strong>유의사항</strong> : ‘아니오’로 평가한 모든 항목에 대해서는 다음 점검 시
          <strong>효과적으로 운영되고 있는</strong> 수준으로 개선하기 위한
          <strong>구체적인 실행계획(Action Plan)</strong>을 작성해야 합니다.
        </p>

      </div>
    </header>


    <!-- 로딩/에러 -->
    <section v-if="loading" class="section"><p class="desc">질문을 불러오는 중…</p></section>
    <section v-else-if="error" class="section"><p class="desc" style="color:#b91c1c">{{ error }}</p></section>

    <!-- 섹션(기준) 렌더링 -->
    <section v-else v-for="(sec, si) in sections" :key="sec.key" class="section">
      <h2 class="section__title">{{ sec.title }}</h2>
      <p class="section__goal" v-if="sec.goal">목표: {{ sec.goal }}</p>

      <div class="t" role="table" :aria-label="`${sec.title} 설문 표`">
        <!-- Head -->
        <div class="t__head" role="row">
          <div class="th th--no" role="columnheader">구분</div>
          <div class="th th--q" role="columnheader">질문항목</div>
          <div class="th th--score" role="columnheader" aria-label="응답 ">응답</div>
          <!-- <div class="th th--extra" role="columnheader">추가 필요 정보</div> -->
          <div class="th th--note" role="columnheader">비고</div>
        </div>

        <!-- Body -->
        <ul class="t__body">

          <li v-for="(q, qi) in sec.items" :key="q.key" class="tr" role="row">
            <div class="td td--no" role="cell">
              {{ sectionStartIndex[si] + qi + 1 }}
            </div>

            <div class="td td--q" role="cell">
              <label class="q-label" :for="idFor(si, qi, 'yn-yes')">{{ q.text }}</label>
            </div>

            <!-- ✅ 점수: Yes / No -->
            <div class="td td--score" role="cell">
              <fieldset class="score-yn" :aria-labelledby="idFor(si, qi, 'label')">
                <legend :id="idFor(si, qi, 'label')" class="sr-only">
                  문항 평가 (Yes / No 선택)
                </legend>

                <!-- Yes -->
                <label class="yn">
                  <input
                    type="radio"
                    :name="`score-${si}-${qi}`"
                    :id="idFor(si, qi, 'yn-yes')"
                    :value="1"
                    v-model="answers[q.key].score"
                    required
                  />
                  <span class="yn__btn yn__btn--yes">Yes</span>
                </label>

                <!-- No -->
                <label class="yn">
                  <input
                    type="radio"
                    :name="`score-${si}-${qi}`"
                    :id="idFor(si, qi, 'yn-no')"
                    :value="0"
                    v-model="answers[q.key].score"
                    required
                  />
                  <span class="yn__btn yn__btn--no">No</span>
                </label>
              </fieldset>
            </div>

            <div class="td td--note" role="cell">
              <input
                type="text"
                class="input"
                placeholder="비고 입력"
                v-model.trim="answers[q.key].note"
              />
            </div>
          </li>
        </ul>
      </div>
    </section>

    <!-- Submit -->
    <footer class="page-foot">
      <button class="btn-submit" type="button" @click="onSubmit">진단하기</button>
    </footer>
  </main>
</template>

<script setup>
import { reactive, ref, onMounted, computed, watch, onBeforeUnmount } from 'vue'
import { useRouter, onBeforeRouteLeave } from 'vue-router'
import { useStore } from 'vuex'
import { selectFreeSurveyList, updateFreeSurvey } from '@/api/survey'

/** ===== 설정 ===== */
// const SURVEY_CODE = 'SURV004'
const STORAGE_KEYS = {
  DRAFT:  'free-survey-draft',     // 작성중 자료(비로그인 포함)
  SNAP:   'free-survey-snapshot',  // 제출 후 결과 페이지 전달용
}

/** (UI) 섹션 메타 */
const CRITERIA_META = {
  '1': { title: '감사위원회(Audit Committee)' },
  '2': { title: '내부감사인(Internal Auditors)' },
  '3': { title: '외부감사인(Independent Auditors)' },
  '4': { title: '최고 경영진(Executive Management)' },
  '5': { title: '이사회(Board of Directors)' },
}

const router = useRouter()
const store  = useStore()
const checkUserId = computed(() => store.state.temp.userInfo)
const isLoggedIn  = computed(() => !!checkUserId.value?.userId)

/** 상태 */
const loading = ref(true)
const error   = ref('')
const sections = ref([])         // [{ key, title, goal, items:[{ key, text, raw }]}]
const answers  = reactive({})    // { [qKey]: { score, extra, note } }

/** util */
const idFor = (si, qi, suffix) => `q-${si}-${qi}-${suffix}`

/** ===== 데이터 가공: 무료/유료 호환 (questionFreeCode || questionCode) ===== */
function groupBySection(rawList) {
  const map = new Map()
  rawList.forEach(row => {
    const sid = String(row.sectionId ?? '')
    if (!map.has(sid)) map.set(sid, [])
    map.get(sid).push(row)
  })
  for (const [sid, arr] of map) {
    arr.sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0))
    map.set(sid, arr.slice(0, 7))
  }
  const keys = [...map.keys()].sort((a, b) => Number(a) - Number(b))
  return keys.map(sid => {
    const meta = CRITERIA_META[sid] || { title: `기준 ${sid}`, goal: '' }
    const rows = map.get(sid) || []
    return {
      key: `sec_${sid}`,
      title: meta.title,
      goal: meta.goal,
      items: rows.map(r => {
        const qKey = r.questionFreeCode || r.questionCode || `row-${r.id}`
        return { key: qKey, text: r.queItem, raw: r }
      }),
    }
  })
}

function hydrateAnswers() {
  sections.value.forEach(sec => {
    sec.items.forEach(q => {
      const r = q.raw || {}

      if (!answers[q.key]) {
        let initialScore = null

        if (isLoggedIn.value) {
          // 🔹 로그인 사용자라도, 'SAVED' 상태의 응답만 복원
          if (r.status === 'SAVED' && (r.score === 0 || r.score === 1)) {
            initialScore = Number(r.score)
          } else {
            initialScore = null   // 저장 이력 없으면 미선택
          }
        } else {
          // 🔹 비로그인(게스트)은 항상 미선택 상태에서 시작
          initialScore = null
        }

        answers[q.key] = {
          score: initialScore,
          extra: r.addInfo ?? '',
          note : r.note ?? '',
        }
      }
    })
  })
}

/** ===== 임시저장(DRAFT) / 스냅샷(SNAPSHOT) ===== */
function serializeAnswers() {
  const out = {}
  for (const k of Object.keys(answers)) {
    const a = answers[k] || {}
    out[k] = { score: a.score ?? null, extra: a.extra || '', note: a.note || '' }
  }
  return out
}
function saveDraft() {
  const draft = {
    // surveyCode: SURVEY_CODE,
    ts: Date.now(),
    answers: serializeAnswers(),
    // 결과 재구성을 위해 질문 메타도 최소한 저장
    items: sections.value.flatMap(sec =>
      sec.items.map(q => ({
        sectionId: q.raw.sectionId,
        sectionTitle: sec.title,
        questionCode: q.raw.questionCode || null,
        questionFreeCode: q.raw.questionFreeCode || null,
        queItem: q.text
      }))
    ),
  }
  localStorage.setItem(STORAGE_KEYS.DRAFT, JSON.stringify(draft))
}
function clearDraft() { localStorage.removeItem(STORAGE_KEYS.DRAFT) }
function saveSnapshotLocal(snapshot) {
  localStorage.setItem(STORAGE_KEYS.SNAP, JSON.stringify(snapshot))
}
function clearSnapshot() { localStorage.removeItem(STORAGE_KEYS.SNAP) }
function clearAllLocal() { clearDraft(); clearSnapshot() }

/** 변경 시 자동 임시저장(너무 잦은 저장을 막으려면 throttle 구현 가능) */
watch(answers, saveDraft, { deep: true })

/** ===== API ===== */
// async function fetchSurvey() {
//   try {
//     loading.value = true
//     error.value = ''
//     const res = await selectFreeSurveyList({
//       // surveyCode: SURVEY_CODE,                    // 백엔드가 무시해도 호환을 위해 보냄
//       userId: checkUserId.value?.userId || undefined, // 비로그인 허용
//     })
//     if (!res || res.result !== 'ok' || !Array.isArray(res.data)) {
//       throw new Error('잘못된 응답 형식')
//     }
//     sections.value = groupBySection(res.data)
//     hydrateAnswers()

//     // 혹시 남아있는 오래된 draft가 있으면 질문 키가 맞는지 확인 후 병합할 수도 있음.
//     // 간단히: 서버 프리필 > draft (원하면 여기 병합 로직 추가)
//     clearDraft()
//   } catch (e) {
//     console.error(e)
//     error.value = '질문을 불러오지 못했습니다.'
//   } finally {
//     loading.value = false
//   }
// }

async function fetchSurvey() {
  try {
    loading.value = true
    error.value = ''
    const res = await selectFreeSurveyList({
      userId: checkUserId.value?.userId || undefined,
    })
    if (!res || res.result !== 'ok' || !Array.isArray(res.data)) {
      throw new Error('잘못된 응답 형식')
    }
    sections.value = groupBySection(res.data)
    hydrateAnswers()
    clearDraft()
  } catch (e) {
    console.error(e)
    error.value = '질문을 불러오지 못했습니다.'
  } finally {
    loading.value = false
  }
}

function validateSectionsBeforeSubmit() {
  // sections: ref([...]), answers: reactive({})
  for (let si = 0; si < sections.value.length; si++) {
    const sec = sections.value[si]

    // 이 섹션에서 하나라도 선택된 문항이 있는지 체크 (0=No, 1=Yes 둘 다 선택으로 인정)
    const hasAnswered = sec.items.some(q => {
      const a = answers[q.key]
      return a != null && (a.score === 0 || a.score === 1)
    })

    if (!hasAnswered) {
      alert(`"${sec.title}" 섹션에서 최소 1개 문항은 선택해 주세요.`)

      // 해당 섹션으로 스크롤 (선택)
      const sectionEls = document.querySelectorAll('.section')
      const target = sectionEls[si]
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }

      return false
    }
  }

  return true
}


/** ===== 제출 로직: 로그인/비로그인 공용 ===== */
async function onSubmit() {

  if (!validateSectionsBeforeSubmit()) {
    return
  }
  const payload = []
  sections.value.forEach(sec => {
    sec.items.forEach(q => {
      const a = answers[q.key] || {}
      const r = q.raw || {}
      payload.push({
        userId: checkUserId.value?.userId || null,
        sectionId: String(r.sectionId ?? ''),
        questionCode: r.questionCode || null,
        questionFreeCode: r.questionFreeCode || null,
        score: (a.score === 0 || a.score === 1) ? Number(a.score) : null,
        addInfo: a.extra || '',
        note: a.note || '',
        status: 'SAVED',
      })
    })
  })

  const total = payload.reduce((s, x) => s + (x.score || 0), 0)
  const avg = payload.length ? total / payload.length : 0
  const snapshot = {
    form: 'free-survey',
    submittedAt: new Date().toISOString(),
    totalScore: total,
    averageScore: avg,
    items: sections.value.flatMap(sec =>
      sec.items.map(q => {
        const ans = answers[q.key] || {}
        const rawScore = ans.score

        return {
          sectionId: q.raw.sectionId,
          sectionTitle: sec.title,
          questionCode: q.raw.questionCode || null,
          questionFreeCode: q.raw.questionFreeCode || null,
          queItem: q.text,
          score: (rawScore === 0 || rawScore === 1) ? rawScore : null,
          // addInfo: ans.extra || '',
          note: ans.note || '',
        }
      })
    ),
  }


  try {
    loading.value = true
   if (isLoggedIn.value) {
     // 로그인 사용자(무료/유료 모두) → 서버에 저장
     await updateFreeSurvey(payload)
   } else {
     // 비로그인(게스트) → 서버 호출 없이 로컬로만 처리
   }

    saveSnapshotLocal(snapshot)
    clearDraft()
    alert('설문이 저장되었습니다.')
    router.push('/surveyfreeresult')
  } catch (e) {
    console.error(e)
    alert('설문 저장 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.')
  } finally {
    loading.value = false
  }
}




/** ===== 라우팅/언로드 시 정리 =====
 * - 결과 페이지로 이동할 때는 snapshot을 유지하고 draft만 삭제
 * - 그 외 페이지로 벗어나면 draft/snapshot 모두 삭제
 */
onBeforeRouteLeave((to) => {

 // 결과 페이지로 갈 때는 snapshot 유지
 if (to?.name === 'surveyfreeresult' || to?.path === '/surveyfreeresult') return
 // 비로그인(게스트)만 다른 페이지로 갈 때 데이터 삭제
 if (!isLoggedIn.value) clearAllLocal()
})

/** 새로고침/탭 닫기 등 페이지 이탈 */
function handleBeforeUnload() {

  if (!isLoggedIn.value) clearAllLocal()
}
window.addEventListener('beforeunload', handleBeforeUnload)
onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
})

// function restoreFromSnapshot() {
//   const raw = sessionStorage.getItem('audit-committee-survey')
//   if (!raw) return

//   try {
//     const snap = JSON.parse(raw)

//     // 다른 설문 코드랑 섞이지 않도록 체크
//     if (snap.surveyCode !== SURVEY_CODE) return
//     if (!Array.isArray(snap.items)) return

//     // questionCode → score 매핑
//     const scoreMap = new Map(
//       snap.items.map(it => [it.questionCode, it.score])
//     )

//     sections.value.forEach(sec => {
//       sec.items.forEach(q => {
//         const savedScore = scoreMap.get(q.key)

//         // 저장된 값이 있으면 그대로 덮어쓰기 (0, 1, null 모두 허용)
//         if (savedScore === 0 || savedScore === 1 || savedScore === null) {
//           if (!answers[q.key]) {
//             answers[q.key] = {
//               score: savedScore,
//               extra: q.raw.addInfo ?? '',
//               note: q.raw.note ?? '',
//             }
//           } else {
//             answers[q.key].score = savedScore
//           }
//         }
//       })
//     })
//   } catch (e) {
//     console.error('snapshot 복원 실패', e)
//   }
// }

function restoreFromSnapshot() {
  // 🔹 무료 설문 스냅샷은 localStorage + STORAGE_KEYS.SNAP 사용
  const raw = localStorage.getItem(STORAGE_KEYS.SNAP)
  if (!raw) return

  try {
    const snap = JSON.parse(raw)
    if (!snap || !Array.isArray(snap.items)) return

    // questionFreeCode || questionCode → { score, note } 매핑
    const map = new Map()
    snap.items.forEach(it => {
      const key = it.questionFreeCode || it.questionCode
      if (!key) return
      map.set(key, {
        score: (it.score === 0 || it.score === 1) ? it.score : null,
        note: it.note ?? '',
      })
    })

    sections.value.forEach(sec => {
      sec.items.forEach(q => {
        const r = q.raw || {}
        const key = r.questionFreeCode || r.questionCode
        const saved = map.get(key)
        if (!saved) return

        if (!answers[q.key]) {
          answers[q.key] = {
            score: saved.score,
            extra: r.addInfo ?? '',
            note: saved.note,
          }
        } else {
          answers[q.key].score = saved.score
          answers[q.key].note = saved.note
        }
      })
    })
  } catch (e) {
    console.error('free-survey snapshot 복원 실패', e)
  }
}



/** 시작 */
// onMounted(async () => {
//   await fetchSurvey()   // 서버 데이터로 sections, answers 기본 세팅
//   hydrateAnswers()      // 기존 로직 그대로 유지

//   // 🔥 서버가 null을 0으로 바꿔버려도, 우리가 저장해 둔 snapshot으로 최종 덮어쓰기
//   restoreFromSnapshot()
// })


// ✅ onMounted는 이렇게만
onMounted(async () => {
  await fetchSurvey()       // 1차: 서버 값 기준으로 채우고
  restoreFromSnapshot()     // 2차: 우리가 저장해둔 스냅샷으로 최종 덮어쓰기
})

// ✅ 섹션별 시작 인덱스 (누적 번호 계산용)
const sectionStartIndex = computed(() => {
  const arr = []
  let acc = 0

  sections.value.forEach(sec => {
    arr.push(acc)              // 현재 섹션의 시작 번호
    acc += sec.items.length    // 다음 섹션 시작을 위해 누적
  })

  return arr                   // [0, 7, 14, ...] 이런 느낌
})
</script>

<style scoped>
/* ====== 기본 레이아웃/폰트 규격 ====== */
.survey-page {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 20px;
  padding: 20px 16px 40px;
  background: #fff;
  font-family: 'Noto Sans KR', sans-serif;
}

/* 타이틀 규격 */
.title { font-size: 1.625rem; font-weight: 700; }

/* 설명글(p) 규격 */
.desc, .section__goal, .t .td p { font-size: 0.95rem; }

.page-head { 
  display: grid; 
  gap: 8px; 
  margin-bottom: 20px;
}

.title { 
  font-size: 1.625rem; 
  font-weight: 700; 
}

.desc strong {
  font-weight: 700;
}

.desc p { 
  font-size: 0.95rem; 
  line-height: 1.65;
  margin-bottom: 10px;
  /* text-wrap: pretty; */
  word-break: keep-all;
}
.section { display: grid; gap: 12px; }
.section__title { font-size: 1.125rem; font-weight: 700; }

/* ====== 표(Grid) ====== */
.t { display: grid; gap: 0; border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden; }
.t__head {
  display: grid;
  grid-template-columns: 72px 1fr 280px 260px 220px;
  background: #0f172a;
  color: #fff;
}
.th { padding: 12px 12px; font-weight: 700; }
.t__body { display: grid; gap: 0; }
.tr {
  display: grid;
  grid-template-columns: 72px 1fr 280px 260px 220px;
  border-top: 1px solid #e5e7eb;
  background: #fff;
}
.td { padding: 12px; display: flex; align-items: center; }
.td2 {padding: 12px; display: flex; align-items: center; justify-content: center;}
.td--no { justify-content: center; color: #6b7280; }
.td--q { align-items: center; }
.q-label { line-height: 1.6; 
  /* text-wrap: pretty; */
}

/* 점수 라디오 */
.score { display: grid; grid-auto-flow: column; gap: 8px; justify-content: center; align-items: center; }
.radio { display: grid; place-items: center; }
.radio input { position: absolute; opacity: 0; width: 0; height: 0; }
.radio__mark {
  display: inline-flex; width: 36px; height: 36px; border-radius: 50%;
  align-items: center; justify-content: center;
  border: 1px solid #d1d5db; background: #f9fafb; font-weight: 600;
  user-select: none; cursor: pointer;
}
.radio input:checked + .radio__mark {
  background: #1d2088; color: #fff; border-color: #1d2088;
}

/* 입력 필드 */
.input {
  width: 100%; min-height: 38px; padding: 8px 10px; border: 1px solid #e5e7eb;
  border-radius: 8px; font-size: 0.95rem;
}
.input:focus { outline: 2px solid #1d2088; outline-offset: 1px; }

/* 제출 영역 */
.page-foot { display: grid; gap: 10px; justify-items: center; margin-top: 8px; }
.btn-submit {
  padding: 12px 18px; border-radius: 10px; background: #1d2088; color: #fff;
  font-weight: 700; cursor: pointer; border: 1px solid #1d2088;
}
.btn-submit:hover { filter: brightness(0.95); }

/* 반응형 */
@media (max-width: 1200px) {
  .t__head, .tr { grid-template-columns: 60px 1fr 240px 220px 200px; }
}
@media (max-width: 992px) {
  .t__head, .tr { grid-template-columns: 52px 1fr 200px 1fr 1fr; }
}
@media (max-width: 768px) {
  .t__head { display: none; }
  .tr { grid-template-columns: 1fr; gap: 8px; border-top: 1px solid #e5e7eb; padding: 10px 12px; }
  .td--no { justify-content: flex-start; font-weight: 700; }
  .td--q { order: 2; }
  .td--score { order: 3; }
  .td--extra { order: 4; }
  .td--note { order: 5; }
  .td { padding: 0; }
  .score { justify-content: flex-start; }
}

/* 스크린리더 전용 텍스트 */
.sr-only {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0;
}

/* ====== 표(Grid) ====== */
.t { display: grid; gap: 0; border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden; }

/* 헤더/행: 4컬럼 = [번호 | 질문 | 응답버튼 | 비고] */
.t__head {
  display: grid;
  grid-template-columns: 72px 1fr 200px 250px;
  background: #0f172a;
  color: #fff;
  justify-items: center;
}
.th { padding: 12px 12px; font-weight: 700; }

.t__body { display: grid; gap: 0; }
.tr {
  display: grid;
  grid-template-columns: 72px 1fr 200px 250px;
  border-top: 1px solid #e5e7eb;
  background: #fff;
}
.td { padding: 12px; display: flex; align-items: center; }
.td--no { justify-content: center; color: #6b7280; }
.td--q { align-items: center; }
.q-label { line-height: 1.6; }


@media (max-width: 1200px) {
  .t__head, .tr { grid-template-columns: 60px 1fr 260px 200px; }
}
@media (max-width: 992px) {
  .t__head, .tr { grid-template-columns: 52px 1fr 220px 1fr; }
}
@media (max-width: 768px) {
  .t__head { display: none; }
  .tr { grid-template-columns: 1fr; gap: 10px; border-top: 1px solid #e5e7eb; padding: 12px; }
  .td { padding: 0; }
  .td--no { justify-content: flex-start; font-weight: 700; order: 1; }
  .td--q { order: 2; }
  .td--score { order: 3; }
  .td--note { order: 4; }
  .score-ryg { justify-content: flex-start; }
}


/* 짝수행 배경색 */
.t__body .tr:nth-child(even) {
  background: #f7f7f7; /* 너무 진하지 않은 옅은 회색 */
}

/* 회원 전용 안내 상자 */
.member-note {
  background: #f5f5f5;
  padding: 16px 18px;
  border-radius: 6px;
  margin-bottom: 16px;
}

.member-note p {
  margin-bottom: 8px;
}

/* ===== Yes / No 심플 버튼 ===== */
.score-yn {
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
}

.yn {
  position: relative;
  display: flex;
  align-items: center;
}

.yn input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.yn__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 34px;
  height: 34px;
  padding: 0 16px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: #fff;
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  transition: 
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease,
    box-shadow 0.15s ease;
}

/* Hover */
.yn__btn:hover {
  background: #f3f4f6;
}

/* Focus */
.yn input:focus + .yn__btn {
  outline: 2px solid #93c5fd; /* light blue */
  outline-offset: 2px;
}

/* 체크된 버튼 (심플 파란색 강조) */
.yn input:checked + .yn__btn {
  background: #eff6ff;    /* 아주 연한 파란색 */
  border-color: #3b82f6;  /* 파란 보더 */
  color: #1e3a8a;         /* 진파랑 글씨 */
}


</style>
